// Visual building blocks of Guess: the player clock, the microphone meter and the picture frame.
import type { GuessPicture } from "@shared/guess/api";
import { motion } from "motion/react";
import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { Avatar, type Mood } from "../../components/Avatar";
import { Mic, MicOff } from "../../components/Icon";
import type { MicState } from "./controller";

/** 42 → "42"; 9 400 ms → "9.4"; never negative. */
export function clockText(ms: number): string {
  const v = Math.max(0, ms);
  if (v < 10_000) return (Math.floor(v / 100) / 10).toFixed(1);
  return String(Math.ceil(v / 1000 - 1e-9));
}

export function secondsText(ms: number): string {
  return (Math.round(Math.max(0, ms) / 100) / 10).toFixed(1);
}

/** Matches a CSS media query (re-renders on change). */
export function useMedia(query: string): boolean {
  const [m, setM] = useState(() => typeof window !== "undefined" && window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const on = () => setM(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, [query]);
  return m;
}

/** Re-render at ~20 fps while `active` (for running clocks). */
export function useFrameNow(active: boolean): number {
  const [t, setT] = useState(() => performance.now());
  useEffect(() => {
    if (!active) {
      setT(performance.now());
      return;
    }
    let raf = 0;
    let last = 0;
    const loop = (ts: number) => {
      if (ts - last >= 50) {
        last = ts;
        setT(performance.now());
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [active]);
  return t;
}

export function ClockPill({
  name,
  avatar,
  ms,
  active,
  out,
  compact,
  mood,
  penaltyKey,
  align = "left",
  tall,
}: {
  name: string;
  avatar: number;
  ms: number;
  active: boolean;
  out: boolean;
  compact?: boolean;
  mood?: Mood;
  penaltyKey?: number | null;
  align?: "left" | "right";
  /** big vertical card for the side-by-side (TV / laptop) layout */
  tall?: boolean;
}) {
  const urgent = active && !out && ms <= 10_000;
  const bg = out ? "var(--color-ground-3)" : urgent ? "var(--color-bad)" : active ? "var(--color-guess)" : "var(--color-ground-3)";
  const ink = out ? "var(--color-ink-3)" : active || urgent ? "#2a0f00" : "var(--color-ink)";
  const style: CSSProperties = {
    background: bg,
    color: urgent ? "#fff" : ink,
    boxShadow: active && !out ? `0 6px 0 ${urgent ? "#8f1020" : "var(--color-guess-deep)"}` : "0 6px 0 var(--color-ground-2)",
  };
  if (tall) {
    return (
      <motion.div
        layout
        className="relative flex w-full max-w-[280px] flex-col items-center gap-3 rounded-[36px] px-6 py-8 text-center"
        style={style}
        animate={{ scale: active && !out ? 1 : 0.9, opacity: out ? 0.55 : 1 }}
        transition={{ type: "spring", stiffness: 380, damping: 28 }}
        aria-label={`${name}: ${clockText(ms)}`}
      >
        <Avatar index={avatar} size={128} mood={out ? "sad" : mood ?? (active ? "talk" : "still")} disc={false} />
        <div className="w-full truncate text-xl font-extrabold opacity-85">{name}</div>
        <div className={`display tabular-nums leading-none text-[clamp(64px,7vw,110px)] ${urgent ? "animate-[urgent_1s_ease-in-out_infinite]" : ""}`}>{clockText(ms)}</div>
        {penaltyKey ? (
          <motion.span
            key={penaltyKey}
            initial={{ opacity: 1, y: 0, scale: 1 }}
            animate={{ opacity: 0, y: -70, scale: 1.4 }}
            transition={{ duration: 1.1, ease: "easeOut" }}
            className="display pointer-events-none absolute top-6 left-1/2 -translate-x-1/2 text-5xl text-bad"
            style={{ textShadow: "0 3px 0 #fff" }}
          >
            −3
          </motion.span>
        ) : null}
      </motion.div>
    );
  }
  return (
    <motion.div
      layout
      className={`relative flex min-w-0 items-center gap-2.5 rounded-[26px] ${compact ? "px-3 py-2" : "px-4 py-3 sm:px-5 sm:py-4"} ${align === "right" ? "flex-row-reverse text-right" : ""}`}
      style={style}
      animate={{ scale: active && !out ? 1 : 0.94, opacity: out ? 0.55 : 1 }}
      transition={{ type: "spring", stiffness: 380, damping: 28 }}
      aria-label={`${name}: ${clockText(ms)}`}
    >
      <Avatar index={avatar} size={compact ? 34 : 52} mood={out ? "sad" : mood ?? (active ? "talk" : "still")} disc={false} />
      <div className="min-w-0 flex-1">
        <div className={`truncate font-extrabold ${compact ? "text-xs" : "text-sm sm:text-base"} opacity-80`}>{name}</div>
        <div className={`display tabular-nums leading-none ${compact ? "text-2xl" : "text-[40px] sm:text-[52px]"} ${urgent ? "animate-[urgent_1s_ease-in-out_infinite]" : ""}`}>{clockText(ms)}</div>
      </div>
      {penaltyKey ? (
        <motion.span
          key={penaltyKey}
          initial={{ opacity: 1, y: 0, scale: 1 }}
          animate={{ opacity: 0, y: -46, scale: 1.25 }}
          transition={{ duration: 1.1, ease: "easeOut" }}
          className="display pointer-events-none absolute -top-2 left-1/2 -translate-x-1/2 text-3xl text-bad"
          style={{ textShadow: "0 2px 0 #fff" }}
        >
          −3
        </motion.span>
      ) : null}
    </motion.div>
  );
}

/** Five bars that follow the microphone level; the colour says what the game is doing. */
export function MicMeter({ level, state, label }: { level: number; state: MicState; label: ReactNode }) {
  const v = Math.max(0, Math.min(1, (level + 62) / 46));
  const color = state === "checking" ? "var(--color-flame)" : state === "hearing" ? "var(--color-good)" : state === "listening" ? "var(--color-ink-2)" : "var(--color-ink-3)";
  return (
    <div className="flex items-center justify-center gap-3" aria-live="polite">
      <span className="grid h-10 w-10 place-items-center rounded-full" style={{ background: "var(--color-ground-3)", color }}>
        {state === "off" ? <MicOff size={20} /> : <Mic size={20} />}
      </span>
      <span className="flex h-7 items-end gap-1" aria-hidden>
        {[0.15, 0.32, 0.5, 0.68, 0.84].map((t, i) => (
          <span
            key={i}
            className="w-1.5 rounded-full transition-[height,background] duration-75"
            style={{ height: `${8 + i * 4}px`, background: state !== "off" && v > t ? color : "var(--color-ground-4)" }}
          />
        ))}
      </span>
      <span className="min-w-[8ch] text-left font-bold" style={{ color }}>
        {label}
      </span>
    </div>
  );
}

/** The picture inside the glossy orange Guess frame. Emoji rebuses and flags get their own treatment. */
export function PictureCard({ picture, size, dim, hearing }: { picture: GuessPicture | null; size: string; dim?: boolean; hearing?: boolean }) {
  return (
    <motion.div
      className="relative mx-auto aspect-square"
      animate={{ scale: hearing ? 1.018 : 1 }}
      transition={{ type: "spring", stiffness: 300, damping: 18 }}
      style={{
        width: size,
        padding: `max(3px, calc(${size} * 0.018))`,
        borderRadius: `calc(${size} * 0.075)`,
        background: "var(--color-guess)",
        boxShadow: `0 max(3px, calc(${size} * 0.018)) 0 var(--color-guess-deep), inset 0 max(1px, calc(${size} * 0.006)) 0 rgba(255,255,255,.45)`,
      }}
    >
      <div
        className="relative h-full w-full overflow-hidden"
        style={{ borderRadius: `calc(${size} * 0.06)`, background: picture?.kind === "flag" ? "#f4eefc" : "var(--color-ground-2)" }}
      >
        {picture?.kind === "emoji" ? (
          <div className="grid h-full w-full place-items-center p-4 text-center" style={{ fontSize: `calc(${size} / 6.2)`, lineHeight: 1.15 }}>
            <span className="break-words" style={{ fontFamily: "'Apple Color Emoji','Segoe UI Emoji','Noto Color Emoji',sans-serif" }}>
              {picture.emoji}
            </span>
          </div>
        ) : picture?.src ? (
          <img
            src={picture.src}
            alt=""
            draggable={false}
            className={`h-full w-full select-none ${picture.kind === "flag" ? "object-contain p-[8%] drop-shadow-[0_6px_10px_rgba(26,10,49,.25)]" : "object-cover"}`}
          />
        ) : null}
        {dim && <div className="absolute inset-0 bg-[rgba(26,10,49,.55)]" />}
      </div>
    </motion.div>
  );
}
